import React, { useEffect, useState } from 'react';
import './GroupsModal.css';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Fresh, simple modal that reliably fetches groups and allows multi-selection.
export default function GroupsModal({ onClose, onSelect, apiCall, initialSelected, initialFlags }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => {
    if (!initialSelected) return [];
    return Array.isArray(initialSelected) ? initialSelected : [initialSelected];
  });
  const [publicOnly, setPublicOnly] = useState(Boolean(initialFlags && initialFlags.publicOnly));
  const [onlyGroups, setOnlyGroups] = useState(Boolean(initialFlags && initialFlags.onlyGroups));

  // Keep flags in sync if initialFlags prop changes
  useEffect(() => {
    if (initialFlags) {
      setPublicOnly(Boolean(initialFlags.publicOnly));
      setOnlyGroups(Boolean(initialFlags.onlyGroups));
    }
  }, [initialFlags]);

  // Toggle helpers to keep the checkboxes mutually exclusive
  const togglePublic = () => setPublicOnly(prev => {
    const next = !prev;
    if (next) setOnlyGroups(false);
    return next;
  });
  const toggleOnlyGroups = () => setOnlyGroups(prev => {
    const next = !prev;
    if (next) setPublicOnly(false);
    return next;
  });

  const getName = (g) => {
    if (!g) return '';
    return (g.NomeGrupo || g.nomeGrupo || g.Nome || g.nome || g.name || g.Grupo || g.GrupoNome || '') + '';
  };

  const isPublic = (g) => Boolean(g && (g.Publica || g.publica || g.IsPublic || g.isPublic));
  const hasGroupTypeField = (g) => Boolean(g && ('Tipo' in g || 'tipo' in g || 'IsGroup' in g || 'isGroup' in g || 'TipoGrupo' in g || 'Grupo' in g));
  const isGroupType = (g) => Boolean(g && (String(g.Tipo || g.tipo || g.TipoGrupo || g.Grupo || '').toLowerCase() === 'grupo' || g.IsGroup || g.isGroup));
  

  const fetchGroups = async () => {
    setLoading(true);
    setError('');
    try {
      let data;
      if (typeof apiCall === 'function') {
        data = await apiCall('Grupo', 'GET');
      } else {
        const bases = [
          BASE_URL.replace(/\/api\/?$/, ''),
          BASE_URL.replace(/\/api\/?$/, ''),
          '/api'
        ];
        let found = null;
        for (const base of bases) {
          try {
            const url = base.endsWith('/api') ? `${base}/Grupo` : `${base}/api/Grupo`;
            const token = getAuthToken();
            const headers = token ? { 'Authorization': 'Bearer ' + token } : {};
            const res = await fetch(url, { headers });
            if (!res.ok) continue;
            const text = await res.text();
            let parsed;
            try { parsed = JSON.parse(text); } catch { parsed = text; }
            const arr = Array.isArray(parsed) ? parsed : (parsed?.Grupos || parsed?.grupos || []);
            if (arr && arr.length) { found = arr; break; }
          } catch (e) { /* try next */ }
        }
        data = found || [];
      }

      const arr = Array.isArray(data) ? data : (data?.Grupos || data?.grupos || []);
      setGroups(arr || []);
      if (!arr || arr.length === 0) setError('Nenhum grupo encontrado');
    } catch (err) {
      console.error('Erro ao carregar grupos (modal)', err);
      setError('Não foi possível carregar grupos.');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  const filtered = groups.filter(g => {
    const name = getName(g).toLowerCase();
    if (query && !name.includes(query.toLowerCase())) return false;
    // publicOnly should not change the listed groups — it only affects posting behavior, not the visible list
    // if (publicOnly && !isPublic(g)) return false;
    if (onlyGroups) {
      // if there is a type field, enforce it, otherwise keep item (avoid removing unknown shapes)
      if (hasGroupTypeField(g) && !isGroupType(g)) return false;
    }
    return true;
  });

  const toggle = (g) => {
    const id = g.Id || g.id || g.GrupoId || getName(g);
    setSelected(prev => {
      const already = prev.some(s => (s.Id || s.id || s.GrupoId || getName(s)) == id);
      const next = already ? prev.filter(s => (s.Id || s.id || s.GrupoId || getName(s)) != id) : [...prev, g];
      if (onlyGroups && next.length === 0) {
        setPublicOnly(true);
        setOnlyGroups(false);
      }
      return next;
    });
  };

  return (
    <div className="groups-modal-overlay" role="dialog" aria-modal="true" onClick={(e)=>{ if (e.target.classList && e.target.classList.contains('groups-modal-overlay')) onClose(); }}>
      <div className="groups-modal" onClick={(e)=>e.stopPropagation()}>
        <div className="groups-modal-header">
          <div style={{ fontWeight: 700 }}>Selecionar Grupo</div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div style={{ marginTop: 8 }}>
          <input className="groups-modal-search" placeholder="Pesquisar grupos..." value={query} onChange={(e)=>setQuery(e.target.value)} />

          <div className="groups-filter-row" style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center' }}>
            <label className="groups-filter-item" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={publicOnly} onChange={togglePublic} />
              <span>Publica + Grupos</span>
              <span className="groups-filter-alert" title="Atenção" tabIndex={0} aria-label="Informação: Publica + Grupos">
                !
                <div className="groups-filter-tooltip" role="tooltip">
                  Esta opção torna a postagem pública e a envia também para os grupos selecionados abaixo. Para postar apenas em grupos, marque 'Somente grupos'.
                </div>
              </span>
            </label>

            <label className="groups-filter-item" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyGroups} onChange={toggleOnlyGroups} />
              <span>Somente grupos</span>
            </label>

            {/* small helper */}
            <div style={{ marginLeft: 'auto', color: '#666', fontSize: 12 }}>Filtros</div>
          </div>
        </div>

        {/* Mode container: shows either Somente grupos + selected or Público only or default selected chips */}
        <div className="groups-mode-container">
          {onlyGroups ? (
            <div className="groups-mode-block">
              <div className="groups-mode-label">Somente grupos</div>
              <div className="groups-modal-selected-container">
                {selected.map(s => (
                  <div key={(s.Id||s.id||s.GrupoId||getName(s))} className="groups-chip">
                    <span className="groups-chip-name">{getName(s)}</span>
                    <button className="groups-chip-remove" onClick={(e)=>{ e.stopPropagation(); setSelected(prev => { const next = prev.filter(x => (x.Id||x.id||x.GrupoId||getName(x)) !== (s.Id||s.id||s.GrupoId||getName(s))); if (onlyGroups && next.length === 0) { setPublicOnly(true); setOnlyGroups(false); } return next; }); }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          ) : publicOnly ? (
            <div className="groups-mode-block">
              <div className="groups-mode-label">Público</div>
              {/* still show selected groups below the label */}
              <div className="groups-modal-selected-container">
                {selected.map(s => (
                  <div key={(s.Id||s.id||s.GrupoId||getName(s))} className="groups-chip">
                    <span className="groups-chip-name">{getName(s)}</span>
                    <button className="groups-chip-remove" onClick={(e)=>{ e.stopPropagation(); setSelected(prev => { const next = prev.filter(x => (x.Id||x.id||x.GrupoId||getName(x)) !== (s.Id||s.id||s.GrupoId||getName(s))); if (onlyGroups && next.length === 0) { setPublicOnly(true); setOnlyGroups(false); } return next; }); }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="groups-modal-selected-container">
              {selected.map(s => (
                <div key={(s.Id||s.id||s.GrupoId||getName(s))} className="groups-chip">
                  <span className="groups-chip-name">{getName(s)}</span>
                  <button className="groups-chip-remove" onClick={(e)=>{ e.stopPropagation(); setSelected(prev => { const next = prev.filter(x => (x.Id||x.id||x.GrupoId||getName(x)) !== (s.Id||s.id||s.GrupoId||getName(s))); if (onlyGroups && next.length === 0) { setPublicOnly(true); setOnlyGroups(false); } return next; }); }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="groups-modal-body">
          {loading && <div className="groups-modal-empty">Carregando...</div>}
          {error && !loading && <div className="groups-modal-empty">{error}</div>}
          {!loading && !error && filtered.length === 0 && <div className="groups-modal-empty">Nenhum grupo encontrado</div>}

          <ul className="groups-modal-list">
            {filtered.map(g => {
              const id = g.Id || g.id || g.GrupoId || getName(g);
              const isSel = selected.some(s => (s.Id||s.id||s.GrupoId||getName(s)) == (g.Id||g.id||g.GrupoId||getName(g)));
              return (
                <li key={id} className={`groups-modal-item ${isSel ? 'selected' : ''}`} onClick={()=>toggle(g)} onKeyDown={(e)=>{ if (e.key === 'Enter') toggle(g); }} tabIndex={0}>
                  <div className="groups-modal-item-title">{getName(g)}</div>
                  {g.Descricao && <div className="groups-modal-item-desc">{g.Descricao}</div>}
                </li>
              );
            })}
          </ul>

          <div className="groups-modal-footer">
            <button className="modal-cancel-btn" onClick={onClose}>Cancelar</button>
            <button className="modal-select-btn" onClick={()=>{ onSelect && onSelect(selected, { publicOnly, onlyGroups }); onClose && onClose(); }} disabled={!selected || selected.length === 0}>Selecionar {selected.length>0 ? `(${selected.length})` : ''}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
